import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Star, Check, X as XIcon, Trash2, Link as LinkIcon, Copy, Plus, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ConfirmDialog";

const STATUS_PILL = {
  pending: "bg-gold/15 text-gold-deep",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-navy/5 text-navy/60",
};

function PageHeader({ overline, title, action }) {
  return (
    <div className="border-b border-navy/10 px-8 py-8 bg-cream flex items-end justify-between">
      <div>
        <div className="overline text-gold-deep">{overline}</div>
        <h1 className="font-serif text-3xl md:text-4xl text-navy mt-2">{title}</h1>
        <div className="gold-rule mt-4" />
      </div>
      {action}
    </div>
  );
}

export default function AdminEndorsements() {
  const [items, setItems] = useState([]);
  const [invites, setInvites] = useState([]);
  const [tab, setTab] = useState("pending");
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteForm, setInviteForm] = useState({ label: "", target_email: "", expires_days: 30 });
  const [busyId, setBusyId] = useState(null);

  const loadAll = async () => {
    const [e, i] = await Promise.all([api.get("/endorsements"), api.get("/endorsements/invites")]);
    setItems(e.data);
    setInvites(i.data);
  };
  useEffect(() => { loadAll(); }, []);

  const moderate = async (e, action) => {
    setBusyId(e.id);
    try {
      await api.post(`/endorsements/${e.id}/${action}`, { notes: "" });
      toast.success(action === "approve" ? "Approved" : action === "reject" ? "Rejected" : "Updated");
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const toggleFeature = async (e) => {
    setBusyId(e.id);
    try {
      await api.post(`/endorsements/${e.id}/feature`);
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    if (!(await confirmDialog({ title: "Delete this endorsement?", description: "The endorsement will be permanently removed from the wall.", confirmLabel: "Delete", destructive: true }))) return;
    await api.delete(`/endorsements/${id}`);
    await loadAll();
  };

  const createInvite = async (ev) => {
    ev.preventDefault();
    try {
      const { data } = await api.post("/endorsements/invites", {
        label: inviteForm.label,
        target_email: inviteForm.target_email || null,
        expires_days: Number(inviteForm.expires_days) || 30,
      });
      toast.success("Invite link created");
      const link = `${window.location.origin}/endorse/${data.token}`;
      try {
        await navigator.clipboard.writeText(link);
        toast.success("Link copied to clipboard");
      } catch {/* ignore */}
      setInviteForm({ label: "", target_email: "", expires_days: 30 });
      setShowInviteForm(false);
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const revokeInvite = async (token) => {
    if (!(await confirmDialog({ title: "Revoke this invite link?", description: "The link will stop working immediately. The endorser will need a new invite.", confirmLabel: "Revoke", destructive: true }))) return;
    await api.delete(`/endorsements/invites/${token}`);
    await loadAll();
  };

  const copyInvite = (token) => {
    const link = `${window.location.origin}/endorse/${token}`;
    navigator.clipboard.writeText(link);
    toast.success("Link copied");
  };

  const filtered = items.filter((i) => i.status === tab);
  const counts = {
    pending: items.filter((i) => i.status === "pending").length,
    approved: items.filter((i) => i.status === "approved").length,
    rejected: items.filter((i) => i.status === "rejected").length,
  };

  return (
    <div data-testid="admin-endorsements-page">
      <PageHeader
        overline="Social proof"
        title="Endorsements"
        action={
          <button
            onClick={() => setShowInviteForm(true)}
            data-testid="endorsements-new-invite"
            className="bg-navy text-gold px-4 py-2.5 text-xs uppercase tracking-widest hover:bg-navy-600 flex items-center gap-2"
          >
            <LinkIcon className="h-3.5 w-3.5" /> New invite link
          </button>
        }
      />
      <div className="p-8 space-y-10">
        {/* Tabs */}
        <div className="flex gap-1 border-b border-navy/10">
          {["pending", "approved", "rejected"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`endorsements-tab-${t}`}
              className={`px-4 py-2.5 text-xs uppercase tracking-widest border-b-2 -mb-px ${
                tab === t ? "border-gold text-navy" : "border-transparent text-navy/50 hover:text-navy"
              }`}
            >
              {t} ({counts[t]})
            </button>
          ))}
        </div>

        {/* List */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.length === 0 && (
            <div className="text-sm italic text-navy/40 col-span-2">No endorsements in this tab.</div>
          )}
          {filtered.map((e) => (
            <div key={e.id} className="bg-white border border-navy/10 p-5" data-testid={`endorsement-row-${e.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-navy">{e.name}</div>
                  <div className="text-xs text-navy/60 truncate">
                    {[e.role, e.organization].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {e.featured && (
                    <span className="text-[10px] uppercase tracking-widest bg-gold text-navy px-2 py-0.5">Featured</span>
                  )}
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${STATUS_PILL[e.status]}`}>
                    {e.status}
                  </span>
                </div>
              </div>
              <p className="mt-4 italic font-serif text-navy/80 leading-relaxed">"{e.quote}"</p>
              <div className="mt-3 text-xs text-navy/40">
                {(e.created_at || "").slice(0, 16).replace("T", " ")}
                {e.invite_token && <span className="ml-2 text-gold-deep">• via invite</span>}
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2 pt-3 border-t border-navy/5">
                {e.status === "pending" && (
                  <>
                    <button
                      onClick={() => moderate(e, "approve")}
                      disabled={busyId === e.id}
                      data-testid={`endorsement-approve-${e.id}`}
                      className="bg-navy text-gold px-3 py-1.5 text-xs uppercase tracking-widest hover:bg-navy-600 flex items-center gap-1.5 disabled:opacity-50"
                    >
                      <Check className="h-3.5 w-3.5" /> Approve
                    </button>
                    <button
                      onClick={() => moderate(e, "reject")}
                      disabled={busyId === e.id}
                      data-testid={`endorsement-reject-${e.id}`}
                      className="border border-navy text-navy px-3 py-1.5 text-xs uppercase tracking-widest hover:bg-navy hover:text-gold flex items-center gap-1.5"
                    >
                      <XIcon className="h-3.5 w-3.5" /> Reject
                    </button>
                  </>
                )}
                {e.status === "approved" && (
                  <>
                    <button
                      onClick={() => toggleFeature(e)}
                      data-testid={`endorsement-feature-${e.id}`}
                      className={`px-3 py-1.5 text-xs uppercase tracking-widest flex items-center gap-1.5 ${
                        e.featured ? "bg-gold text-navy" : "border border-navy/30 text-navy/70 hover:border-gold"
                      }`}
                    >
                      <Star className="h-3.5 w-3.5" /> {e.featured ? "Unfeature" : "Feature"}
                    </button>
                    <button
                      onClick={() => moderate(e, "reject")}
                      data-testid={`endorsement-unapprove-${e.id}`}
                      className="text-xs text-navy/50 hover:text-destructive px-2"
                    >
                      Unpublish
                    </button>
                  </>
                )}
                {e.status === "rejected" && (
                  <button
                    onClick={() => moderate(e, "approve")}
                    data-testid={`endorsement-restore-${e.id}`}
                    className="text-xs uppercase tracking-widest text-gold-deep hover:text-navy"
                  >
                    Restore →
                  </button>
                )}
                <div className="flex-1" />
                {e.linkedin_url && (
                  <a href={e.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-navy/40 hover:text-gold-deep">
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
                <button onClick={() => remove(e.id)} data-testid={`endorsement-delete-${e.id}`} className="text-navy/40 hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Invites */}
        <section data-testid="invites-section">
          <h2 className="overline text-gold-deep mb-3">Invite links ({invites.length})</h2>
          <div className="bg-white border border-navy/10">
            {invites.length === 0 && (
              <div className="p-6 text-sm italic text-navy/40 text-center">No invites yet.</div>
            )}
            {invites.map((inv) => (
              <div key={inv.token} className="border-b border-navy/5 last:border-b-0 p-4 flex items-center gap-4" data-testid={`invite-${inv.token}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-navy">{inv.label}</div>
                  <div className="text-xs text-navy/50">
                    {inv.target_email || "no email"} ·
                    {inv.used ? " used" : " unused"}
                    {inv.expires_at && ` · expires ${inv.expires_at.slice(0, 10)}`}
                  </div>
                </div>
                {!inv.used && (
                  <button onClick={() => copyInvite(inv.token)} data-testid={`invite-copy-${inv.token}`} className="text-xs text-navy/60 hover:text-gold-deep flex items-center gap-1.5">
                    <Copy className="h-3.5 w-3.5" /> Copy link
                  </button>
                )}
                <button onClick={() => revokeInvite(inv.token)} data-testid={`invite-revoke-${inv.token}`} className="text-navy/40 hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </section>
      </div>

      {showInviteForm && (
        <div className="fixed inset-0 z-50 bg-navy/40 flex items-center justify-center p-4" data-testid="invite-form-modal">
          <form onSubmit={createInvite} className="bg-white max-w-md w-full p-8">
            <div className="flex justify-between mb-6">
              <div>
                <div className="overline text-gold-deep">New invite link</div>
                <h2 className="font-serif text-2xl text-navy mt-1">Invite endorser</h2>
              </div>
              <button type="button" onClick={() => setShowInviteForm(false)} className="text-navy/40">
                <XIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-5">
              <div>
                <label className="overline text-navy/60">Label * (your private note)</label>
                <input
                  required
                  data-testid="invite-input-label"
                  value={inviteForm.label}
                  onChange={(e) => setInviteForm({ ...inviteForm, label: e.target.value })}
                  placeholder="e.g. John Smith, EBRD"
                  className="w-full mt-2 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold"
                />
              </div>
              <div>
                <label className="overline text-navy/60">Target email (optional)</label>
                <input
                  type="email"
                  data-testid="invite-input-email"
                  value={inviteForm.target_email}
                  onChange={(e) => setInviteForm({ ...inviteForm, target_email: e.target.value })}
                  className="w-full mt-2 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold"
                />
              </div>
              <div>
                <label className="overline text-navy/60">Expires in (days)</label>
                <input
                  type="number"
                  min="0"
                  data-testid="invite-input-expires"
                  value={inviteForm.expires_days}
                  onChange={(e) => setInviteForm({ ...inviteForm, expires_days: e.target.value })}
                  className="w-full mt-2 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold"
                />
                <div className="text-xs text-navy/40 mt-1">Use 0 for no expiry.</div>
              </div>
            </div>
            <div className="mt-8 flex justify-end gap-3">
              <button type="button" onClick={() => setShowInviteForm(false)} className="px-5 py-2.5 text-xs uppercase tracking-widest text-navy/70">Cancel</button>
              <button type="submit" data-testid="invite-create-save" className="bg-navy text-gold px-6 py-2.5 text-xs uppercase tracking-widest hover:bg-navy-600 flex items-center gap-2">
                <Plus className="h-3.5 w-3.5" /> Create link
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
